import {useRef, useState} from 'react';
import {AnimatePresence} from 'motion/react';
import {publishEvent} from '@udemy-v2/event-tracking';

import assetLoader from '@/utils/asset-loader';
import {DiscoveryItemClickEvent} from '../events';
import styles from './video-player.module.scss';

export interface VideoPlayerProps {
    videoSrc: string;
    posterSrc?: string;
    className?: string;
    videoId?: number;
    position?: number;
    shouldTrackInteractions?: boolean;
}

export default function VideoPlayer({
    videoSrc,
    className,
    posterSrc = undefined,
    videoId,
    position,
    shouldTrackInteractions = false,
}: VideoPlayerProps) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [, setIsPaused] = useState(true);
    const hasTrackedInteraction = useRef(false);

    const handlePlay = () => {
        if (videoRef.current) {
            videoRef.current.play();
            setIsPlaying(true);
            setIsPaused(false);
        }
    };

    const handlePause = () => {
        if (videoRef.current) {
            videoRef.current.pause();
            setIsPlaying(false);
            setIsPaused(true);
        }
    };

    const trackVideoInteraction = () => {
        if (!hasTrackedInteraction.current && shouldTrackInteractions) {
            hasTrackedInteraction.current = true;

            publishEvent(
                new DiscoveryItemClickEvent({
                    type: 'lecture',
                    id: videoId || 0,
                    trackingId: crypto.randomUUID(),
                    backendSource: 'discovery',
                    position: position || 0,
                    uiRegion: 'social_proof',
                    pageFor: '60dayhustle',
                }),
            );
        }
    };

    const handleContainerClick = (event: React.MouseEvent) => {
        const target = event.target as HTMLElement;
        const playButton = target.closest(`.${styles['play-button']}`);

        if (playButton) {
            trackVideoInteraction();

            if (isPlaying) {
                handlePause();
            } else {
                handlePlay();
            }
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            trackVideoInteraction();
            if (isPlaying) {
                handlePause();
            } else {
                handlePlay();
            }
        }
    };

    const handleVideoPlay = () => {
        setIsPlaying(true);
        setIsPaused(false);
    };

    const handleVideoPause = () => {
        setIsPlaying(false);
        setIsPaused(true);
    };

    const handleVideoEnded = () => {
        if (videoRef.current) {
            videoRef.current.load();
        }
        setIsPlaying(false);
        setIsPaused(true);
    };

    return (
        <div
            className={`${styles['video-container']} ${className || ''}`}
            onClick={handleContainerClick}
            onKeyDown={handleKeyDown}
            role="button"
            tabIndex={0}
            aria-label={isPlaying ? 'Pause video' : 'Play video'}
        >
            <video
                ref={videoRef}
                className={styles.video}
                controls
                controlsList="nodownload"
                playsInline
                poster={posterSrc ? assetLoader({src: posterSrc}) : undefined}
                onPlay={handleVideoPlay}
                onPause={handleVideoPause}
                onEnded={handleVideoEnded}
                data-testid="video-element"
            >
                <source src={assetLoader({src: videoSrc})} type="video/mp4" />
                <p>Your browser does not support the video tag.</p>
            </video>

            <AnimatePresence>
                {!isPlaying && (
                    <div className={styles['play-button']} data-testid="motion-div">
                        <svg
                            width="54"
                            height="54"
                            viewBox="0 0 54 54"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <circle cx="27" cy="27" r="27" fill="white" />
                            <path
                                d="M22.8496 19.001L38.0003 27.3489L22.8496 35.6995L22.8496 27.3503L22.8496 19.001Z"
                                fill="black"
                            />
                        </svg>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}
